import { InputAdornment } from '@mui/material';
import { array, number, object, string } from 'yup';

import { RHFAutocomplete, RHFDatePicker, RHFRadioGroup, RHFReactNumberFormat, RHFTextField, useFactFindConfigStore } from '@zeta-core';
import { IClientBeneficiary } from '@zeta-types';
import { monthlyAnnuallyObject, yesNoObject } from '../common/commons';

const ffConfig = useFactFindConfigStore.getState()?.factFindConfigDetails?.individualFactFindSectionConfig?.benefitsAndInsurance?.subsections;

export const beneficiariesValidations = object().shape({
  id: string(),
  name: object()
    .shape({
      // personalPiiDetails: object().shape({
      //   firstName: string().required('Name is required'),
      // }),
      // enable once we have the personalPiiDetails data
    })
    .nullable()
    .typeError('Name is required'),
  percentage: number().min(0, 'Please enter valid percentage').max(100, 'Please enter valid percentage').nullable(),
});

export const policiesValidations = object().shape({
  id: string(),
  institution: string(),
  premium: number().nullable(),
  insuranceType: string().nullable(),
  sumAssured: number().nullable(),
  deferredPeriodInWeeks: string(),
  startDate: string(),
  endDate: string(),
  description: string(),
  beneficiaries: array().of(beneficiariesValidations),
});

export const employeeBenefitsValidations = object().shape({
  institution: string(),
  premium: number().nullable(),
  policyNumber: string(),
  insuranceType: string(),
  sumAssured: number().nullable(),
  duration: string(),
  startDate: string(),
  endDate: string(),
  description: string(),
});

export const otherBenefitsValidations = object().shape({
  id: string(),
  benefitType: string().nullable(),
  providerName: string(),
  frequency: string(),
  isTaxable: string(),
  benefitValue: number().nullable(),
  startDate: string(),
  endDate: string(),
  description: string(),
});

export const beneficiariesAndInsuranceValidations = object().shape({
  policies: array().of(policiesValidations),
  employeeBenefits: array().of(employeeBenefitsValidations),
  otherBenefits: array().of(otherBenefitsValidations),
});

export const beneficiariesAndInsuranceValidationSchema = object().shape({
  client: beneficiariesAndInsuranceValidations,
  partner: beneficiariesAndInsuranceValidations,
  joint: beneficiariesAndInsuranceValidations,
});

export const beneficiariesDefault = { id: '', name: '', percentage: 0, show: true };

export const policiesDefault = {
  id: '',
  institution: '',
  premium: 0,
  insuranceType: '',
  sumAssured: 0,
  deferredPeriodInWeeks: '',
  startDate: '',
  endDate: '',
  description: '',
  beneficiaries: [beneficiariesDefault],
  show: true,
};

export const employeeBenefitsDefault = {
  institution: '',
  premium: 0,
  policyNumber: '',
  insuranceType: '',
  sumAssured: 0,
  duration: '',
  startDate: '',
  endDate: '',
  description: '',
  show: true,
};

export const otherBenefitsDefault = {
  benefitType: '',
  providerName: '',
  frequency: '',
  isTaxable: '',
  benefitValue: 0,
  startDate: '',
  endDate: '',
  description: '',
  show: true,
};

export const beneficiariesAndInsuranceDefault = { policies: [policiesDefault], employeeBenefits: [], otherBenefits: [] };

export const beneficiariesAndInsuranceDefaultValues = {
  client: beneficiariesAndInsuranceDefault,
  partner: beneficiariesAndInsuranceDefault,
  joint: beneficiariesAndInsuranceDefault,
};

export const POLICIES_TABLE_STRUCTURE = (insuranceTypeOptions: []) => {
  return [
    {
      label: 'Institution',
      name: 'institution',
      component: RHFTextField,
      minWidth: 150,
      width: 150,
      show: ffConfig?.policies?.fields?.institution?.active ?? true,
    },
    {
      label: 'Premium',
      name: 'premium',
      component: RHFReactNumberFormat,
      decimalScale: 2,
      thousandSeparator: true,
      fixedDecimalScale: true,
      prefix: true,
      textAlign: 'right',
      minWidth: 150,
      width: 150,
      show: ffConfig?.policies?.fields?.premium?.active ?? true,
    },
    {
      label: 'Insurance Type',
      name: 'insuranceType',
      component: RHFAutocomplete,
      options: insuranceTypeOptions,
      minWidth: 150,
      width: 150,
      show: ffConfig?.policies?.fields?.insuranceType?.active ?? true,
    },
    {
      label: 'Sum Assured/Monthly Income',
      name: 'sumAssured',
      component: RHFReactNumberFormat,
      decimalScale: 2,
      thousandSeparator: true,
      fixedDecimalScale: true,
      textAlign: 'right',
      prefix: true,
      minWidth: 250,
      width: 250,
      show: ffConfig?.policies?.fields?.sumAssured?.active ?? true,
    },
    {
      label: 'Deferred Period in Weeks',
      name: 'deferredPeriodInWeeks',
      component: RHFTextField,
      minWidth: 250,
      width: 250,
      show: ffConfig?.policies?.fields?.deferredPeriodInWeeks?.active ?? true,
    },

    {
      label: 'Start Date',
      name: 'startDate',
      component: RHFDatePicker,
      minWidth: 200,
      width: 200,
      show: ffConfig?.policies?.fields?.startDate?.active ?? true,
    },
    { label: 'End Date', name: 'endDate', component: RHFDatePicker, minWidth: 200, width: 200, show: ffConfig?.policies?.fields?.endDate?.active ?? true },
    {
      label: 'Description',
      name: 'description',
      component: RHFTextField,
      minWidth: 150,
      width: 150,
      show: ffConfig?.policies?.fields?.description?.active ?? true,
    },
  ];
};

export const OTHER_BENEFITS_TABLE_STRUCTURE = (insuranceTypeOptions: []) => {
  return [
    {
      label: 'Benefit Type',
      name: 'benefitType',
      component: RHFAutocomplete,
      options: insuranceTypeOptions,
      minWidth: 150,
      width: 150,
      show: ffConfig?.other?.fields?.benefitType?.active ?? true,
    },
    {
      label: 'Provider Name',
      name: 'providerName',
      component: RHFTextField,
      minWidth: 150,
      width: 150,
      show: ffConfig?.other?.fields?.providerName?.active ?? true,
    },
    {
      label: 'Frequency',
      name: 'frequency',
      component: RHFRadioGroup,
      options: monthlyAnnuallyObject,
      row: true,
      minWidth: 250,
      width: 250,
      show: ffConfig?.other?.fields?.frequency?.active ?? true,
    },
    {
      label: 'Is Taxable?',
      name: 'isTaxable',
      component: RHFRadioGroup,
      options: yesNoObject,
      row: true,
      minWidth: 200,
      width: 200,
      show: ffConfig?.other?.fields?.isTaxable?.active ?? true,
    },
    {
      label: 'Benefit Value',
      name: 'benefitValue',
      component: RHFReactNumberFormat,
      decimalScale: 2,
      thousandSeparator: true,
      fixedDecimalScale: true,
      textAlign: 'right',
      prefix: true,
      minWidth: 150,
      width: 150,
      show: ffConfig?.other?.fields?.benefitValue?.active ?? true,
    },

    { label: 'Start Date', name: 'startDate', component: RHFDatePicker, minWidth: 200, width: 200, show: ffConfig?.other?.fields?.startDate?.active ?? true },
    { label: 'End Date', name: 'endDate', component: RHFDatePicker, minWidth: 200, width: 200, show: ffConfig?.other?.fields?.endDate?.active ?? true },
    {
      label: 'Description',
      name: 'description',
      component: RHFTextField,
      minWidth: 150,
      width: 150,
      show: ffConfig?.other?.fields?.description?.active ?? true,
    },
    { label: '', name: 'action', component: RHFReactNumberFormat, minWidth: 50, width: 50, show: true },
  ];
};

export const EMPLOYEE_BENEFITS_TABLE_STRUCTURE = [
  {
    label: 'Institution',
    name: 'institution',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.employee?.fields?.institution?.active ?? true,
  },
  { label: 'Premium', name: 'premium', component: RHFTextField, minWidth: 150, width: 150, show: ffConfig?.employee?.fields?.premium?.active ?? true },
  {
    label: 'Policy Number',
    name: 'policyNumber',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.employee?.fields?.policyNumber?.active ?? true,
  },
  {
    label: 'Sum Assured/Monthly Income',
    name: 'sumAssured',
    component: RHFReactNumberFormat,
    decimalScale: 2,
    thousandSeparator: true,
    fixedDecimalScale: true,
    textAlign: 'right',
    prefix: true,
    minWidth: 250,
    width: 250,
    show: ffConfig?.employee?.fields?.sumAssured?.active ?? true,
  },
  { label: 'Duration', name: 'duration', component: RHFTextField, minWidth: 150, width: 150, show: ffConfig?.employee?.fields?.duration?.active ?? true },

  {
    label: 'Start Date',
    name: 'startDate',
    component: RHFDatePicker,
    minWidth: 200,
    width: 200,
    height: '100%',
    show: ffConfig?.employee?.fields?.startDate?.active ?? true,
  },
  { label: 'End Date', name: 'endDate', component: RHFDatePicker, minWidth: 200, width: 200, show: ffConfig?.employee?.fields?.endDate?.active ?? true },
  {
    label: 'Description',
    name: 'description',
    component: RHFTextField,
    minWidth: 150,
    width: 150,
    show: ffConfig?.employee?.fields?.description?.active ?? true,
  },
  { label: '', name: 'action', component: RHFReactNumberFormat, minWidth: 50, width: 50, show: true },
];

export const getBeneficiaryTableStructure = (beneficiaryOption: IClientBeneficiary[] | null) => {
  return [
    {
      label: 'Name',
      name: 'name',
      component: RHFAutocomplete,
      minWidth: 150,
      width: 150,
      options: beneficiaryOption || [],
      getOptionLabel: (option: IClientBeneficiary) => {
        return option?.personalPiiDetails?.firstName || option?.name || '';
      },
      show: ffConfig?.beneficiary?.fields?.name?.active ?? true,
    },
    {
      label: 'Percentage',
      name: 'percentage',
      component: RHFReactNumberFormat,
      decimalScale: 2,
      textAlign: 'right',
      InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
      minWidth: 150,
      width: 150,
      show: ffConfig?.beneficiary?.fields?.percentage?.active ?? true,
    },
    { label: '', name: 'action', component: RHFReactNumberFormat, minWidth: 50, width: 50, show: true },
  ];
};
